import React from "react";
import Layout from "@/components/Layout";
import { Check, ArrowRight, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

const About = () => {
  return (
    <Layout>
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold font-display mb-4">Sobre a LexPrompt</h1>
            <p className="text-gray-600 max-w-xl mx-auto">
              Transformando a advocacia com inteligência artificial
            </p>
          </div>

          {/* Nossa Missão */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h2 className="text-2xl font-bold mb-4 text-lawyer-primary">Nossa Missão</h2>
              <p className="text-gray-600 mb-6">
                A LexPrompt nasceu da necessidade de otimizar o trabalho jurídico em um cenário cada vez mais exigente e tecnológico. Nossa missão é democratizar o acesso à tecnologia de IA para advogados, permitindo que profissionais de todos os tamanhos de escritórios possam aumentar sua produtividade e qualidade de trabalho.
              </p>
              <p className="text-gray-600">
                Acreditamos que a inteligência artificial não deve substituir advogados, mas sim potencializar suas habilidades, permitindo que foquem no que realmente importa: a estratégia jurídica e o atendimento personalizado aos clientes.
              </p>
            </div>
            <div className="bg-lawyer-light p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-lawyer-primary">Nossos Valores</h3>
              <ul className="space-y-3">
                {[
                  "Excelência jurídica em primeiro lugar",
                  "Tecnologia a serviço do Direito",
                  "Compromisso com a ética profissional",
                  "Inovação constante",
                  "Respeito ao sigilo e à privacidade"
                ].map((value, index) => (
                  <li key={index} className="flex items-start">
                    <Check className="h-5 w-5 text-lawyer-secondary mr-3 mt-0.5" />
                    <span>{value}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Como a LexPrompt Transforma Sua Prática Jurídica */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold mb-6 text-center text-lawyer-primary">Como a LexPrompt Transforma Sua Prática Jurídica</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  title: "Economia de Tempo",
                  description: "Reduza o tempo gasto em tarefas repetitivas como redigir petições, contratos e pareceres com prompts otimizados para cada situação."
                },
                {
                  title: "Qualidade Consistente",
                  description: "Mantenha um padrão de qualidade em todos os documentos, independentemente de quem os produz em seu escritório."
                },
                {
                  title: "Constante Atualização",
                  description: "Nossos prompts são constantemente atualizados para refletir mudanças na legislação e jurisprudência mais recente."
                }
              ].map((item, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                  <h3 className="text-xl font-semibold mb-3 text-lawyer-primary">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Nossa Equipe - Atualizado com as novas imagens */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold mb-6 text-center text-lawyer-primary">Nossa Equipe</h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {[
                {
                  name: "Irene Maisa",
                  role: "Engenheira de Dados e Especialista em Inteligência Artificial",
                  bio: "Irene Maisa é Engenheira de Dados com especialização em Inteligência Artificial. Atualmente, cursa Desenvolvimento de Aplicações de Inteligência Artificial no ISLA-Gaia, no Porto, Portugal. Atua com foco em automação inteligente, sendo responsável pela criação e otimização de prompts que impulsionam modelos de IA em diferentes contextos. Com um perfil técnico e analítico, Irene transforma grandes volumes de dados em soluções inteligentes, combinando engenharia de dados com práticas avançadas de IA para gerar valor real em produtos e processos.",
                  image: "/decidedigital-uploads/7b175945-6fdb-446c-83a5-50d8e40d5e2c.jpeg"
                },
                {
                  name: "Adelino Masioli",
                  role: "Mestre em Engenharia Web e Inteligência Artificial e Desenvolvedor de Soluções com IA",
                  bio: "Adelino Masioli é Mestre em Engenharia Web e Inteligência Artificial pelo ISLA-Gaia, em Portugal, e desenvolvedor fullstack com foco em soluções web inteligentes. Com experiência prática em aplicações SaaS, automações e integração de IA generativa, atua na construção de plataformas modernas que combinam performance, usabilidade e inteligência. Sua missão é clara: aplicar IA de forma acessível e estratégica para otimizar processos, reduzir custos e ampliar resultados da saúde à indústria criativa.",
                  image: "/decidedigital-uploads/c28c2d3d-6671-4a10-a008-52d804785078.jpeg"
                }
              ].map((person, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                  <div className="flex flex-col items-center  mb-4">
                    <Avatar className="w-24 h-24 mb-4 md:mb-0 md:mr-6">
                      <AvatarImage src={person.image} alt={person.name} className="object-cover" />
                      <AvatarFallback>{person.name.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div >
                      <h3 className="text-xl font-semibold mb-1 text-lawyer-primary  text-center">{person.name}</h3>
                      <p className="text-lawyer-secondary font-medium mb-3 text-sm text-center">{person.role}</p>
                    </div>
                  </div>
                  <p className="text-gray-600">{person.bio}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-6 text-lawyer-primary">Comece sua transformação digital</h2>
            <p className="text-gray-600 max-w-2xl mx-auto mb-8">
              Junte-se a centenas de profissionais que já estão utilizando a LexPrompt para revolucionar sua prática jurídica e oferecer um serviço melhor aos seus clientes.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button asChild className="bg-lawyer-secondary hover:bg-lawyer-gold text-lawyer-dark font-medium transition-colors">
                <Link to="/auth/register" className="flex items-center">
                  Começar Agora
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="border-lawyer-primary text-lawyer-primary hover:bg-lawyer-primary/10">
                <Link to="/pricing" className="flex items-center">
                  <FileText className="mr-2 h-4 w-4" />
                  Ver Planos
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;
